import {
  AgentTurnEvent,
  ConversationMessage,
  ConversationSummary,
  type DocumentId,
} from "@curriculum-studio/core";
import { HttpClient, HttpClientRequest, HttpClientResponse } from "@effect/platform";
import { Data, Effect, Either, Schema } from "effect";

// The review-trigger half of the REST client (U10): `POST /api/review` ({ documentId, kind }), through
// `@effect/platform`'s `HttpClient` so the IO stays in Effect. `kind` is `checks` ("Run checks") — the
// only startable kind (R2: the rubric-refinement `refine` loop and the change-feed-driven `classify`
// pass are both retired). The success body is decoded with a boundary `Schema`. A 409 means a run is
// already in flight for this document (single-flight, D4) and a 503 means the agent is dormant (no
// `ANTHROPIC_API_KEY`, D6) — each is surfaced as a tagged error so the UI can show a spinner /
// unavailable notice rather than failing.

export type ReviewKind = "checks";

const ReviewSuccess = Schema.Struct({ status: Schema.Literal("started") });

// The 409/503 bodies are the server's tagged-error schema ({ _tag, kind, message }); decode the
// `message` through a permissive boundary `Schema` and fall back to a literal when the body is
// absent or malformed, rather than discarding the server's wording entirely.
const ReviewErrorBody = Schema.Struct({ message: Schema.optional(Schema.String) });

const messageOrFallback = (response: HttpClientResponse.HttpClientResponse, fallback: string) =>
  HttpClientResponse.schemaBodyJson(ReviewErrorBody)(response).pipe(
    Effect.map((body) => body.message ?? fallback),
    Effect.orElseSucceed(() => fallback),
  );

export class ReviewAlreadyRunning extends Data.TaggedError("ReviewAlreadyRunning")<{
  readonly message: string;
}> {}

export class ReviewUnavailable extends Data.TaggedError("ReviewUnavailable")<{
  readonly message: string;
}> {}

export const startReview = (documentId: DocumentId, kind: ReviewKind) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.post("/api/review").pipe(
        HttpClientRequest.bodyUnsafeJson({ documentId, kind }),
      ),
    );
    if (response.status === 409) {
      const message = yield* messageOrFallback(
        response,
        "A review run is already in flight for this document.",
      );
      return yield* Effect.fail(new ReviewAlreadyRunning({ message }));
    }
    if (response.status === 503) {
      const message = yield* messageOrFallback(
        response,
        "Auditing is unavailable — the review agent is not configured.",
      );
      return yield* Effect.fail(new ReviewUnavailable({ message }));
    }
    return yield* HttpClientResponse.schemaBodyJson(ReviewSuccess)(response);
  }).pipe(Effect.scoped);

// The "Stop" half of the review client (U6): `POST /api/review/cancel` ({ documentId }) interrupts the
// in-flight run. Both outcomes are 200s — `cancelled` (a run was stopped) or `not-running` (nothing was
// in flight) — so there is no error variant; the UI's running state clears off the WS `running: false`
// the cancel triggers, not off this response body.
const CancelReviewSuccess = Schema.Struct({
  status: Schema.Literal("cancelled", "not-running"),
});

export const cancelReview = (documentId: DocumentId) =>
  Effect.gen(function* () {
    const client = yield* HttpClient.HttpClient;
    const response = yield* client.execute(
      HttpClientRequest.post("/api/review/cancel").pipe(
        HttpClientRequest.bodyUnsafeJson({ documentId }),
      ),
    );
    return yield* HttpClientResponse.schemaBodyJson(CancelReviewSuccess)(response);
  }).pipe(Effect.scoped);

// The session-backed generative-agent turn (ADR-0038 Unit 6, Q9): `POST /api/agent/turn` replaces
// every deleted bespoke NDJSON route (`/api/chat/stream`, `/api/regenerate/stream`, the one-shot
// `/api/regenerate`) with one streaming surface. `skill` would pin a direct skill invocation (a named
// skill plus an anchoring `target`) — a dormant capability of the transport with no live caller today
// (the retired `regenerate-span` skill was the last one); the live path is the Expert's free-form chat
// turn (model election), taken whenever `skill` is absent. Read with the same raw-`fetch` reader + `AbortController`
// discipline the deleted routes used — the streaming body is outside the Effect `HttpClient` decode
// path — but each line now decodes through the SAME `Schema` (`AgentTurnEvent`, from `core`) the
// server encodes with (R4 — parse, don't validate): drift between the two sides becomes a decode-time
// failure, never a silently-skipped line, so a malformed frame ends the turn with `{_tag:"error"}`
// rather than dropping content unnoticed.
export interface AgentTurnHandlers {
  readonly onProse?: (text: string) => void;
  readonly onStatus?: (text: string) => void;
  readonly onTool?: (name: string, phase: "call" | "result") => void;
}

// `disconnected` is the transport-loss outcome: the NDJSON stream ended (or threw) WITHOUT the
// server's terminal `result`/`error` frame ever arriving. The turn's drain runs detached server-side
// (KD8), so the turn is very likely STILL RUNNING — the caller should reattach (the model adapter's
// reconnect path tails the run's SSE), never surface it as a dead-end error whose "Try again" then
// collides with the still-held single-flight claim ("already in flight" — the reported bug).
// `error` stays for genuine dead-ends: the POST itself refused (non-2xx — including that 409), or a
// malformed frame (client/server contract drift, R4).
export type AgentTurnOutcome =
  | { readonly _tag: "completed" }
  | { readonly _tag: "failed"; readonly reason?: string }
  | { readonly _tag: "disconnected" }
  | { readonly _tag: "error"; readonly message: string };

// The exact refusal reason `agent-turn-route.ts`'s single-flight claim streams (its one `TurnError`
// frame) when a second turn POSTs for a conversation whose prior turn hasn't released the claim yet —
// e.g. a cancel-then-immediately-send: `cancelAgentTurn` no longer awaits the claim being freed
// (`Fiber.interruptFork` server-side), so the follow-up's POST can race the still-in-flight release.
// `model-adapter.ts` matches this exact string to retry the send instead of treating it as a dead-end
// refusal.
export const TURN_ALREADY_IN_FLIGHT_MESSAGE = "a turn is already in flight for this conversation";

// Generous wall clock, carried over from the deleted `streamChat`'s bound: a whole-curriculum sweep
// or a check-amend loop can run several model calls deep. This abort is a last-resort guard against a
// wedged fetch, not the working ceiling — the server's own idle-activity timeout
// (`agent-service.ts`'s `TURN_IDLE_TIMEOUT`, 12 minutes) ends a stalled turn with a friendly
// `{_tag:"error"}` long before this fires.
const AGENT_TURN_TIMEOUT_MS = 900_000;

// Fold one NDJSON `AgentTurnEvent` stream (the turn POST's response, or the reconnection `/watch`
// GET's — both speak the identical wire contract) onto the handler callbacks, returning the turn's
// outcome. Shared by `streamAgentTurn` and `watchAgentTurn` so a live turn and a reattached one are
// decoded identically (R4 — parse, don't validate; a malformed frame ends the turn with `error`).
const consumeTurnStream = async (
  response: Response,
  handlers: AgentTurnHandlers,
): Promise<AgentTurnOutcome> => {
  let outcome: AgentTurnOutcome = { _tag: "disconnected" };
  let decodeFailed = false;
  let terminalReceived = false;
  const handle = (rawLine: string): void => {
    if (decodeFailed) return;
    const trimmed = rawLine.trim();
    if (trimmed.length === 0) return;
    const decoded = Schema.decodeUnknownEither(AgentTurnEvent)(JSON.parse(trimmed));
    if (Either.isLeft(decoded)) {
      outcome = { _tag: "error", message: "malformed frame from the server" };
      decodeFailed = true;
      return;
    }
    const event = decoded.right;
    switch (event._tag) {
      case "prose":
        handlers.onProse?.(event.text);
        return;
      case "status":
        handlers.onStatus?.(event.text);
        return;
      case "tool":
        handlers.onTool?.(event.name, event.phase);
        return;
      case "result":
        outcome =
          event.outcome === "completed"
            ? { _tag: "completed" }
            : event.reason === undefined
              ? { _tag: "failed" }
              : { _tag: "failed", reason: event.reason };
        terminalReceived = true;
        return;
      case "error":
        outcome = { _tag: "error", message: event.reason };
        terminalReceived = true;
        return;
      case "ping":
        return;
    }
  };
  try {
    if (!response.ok || response.body === null)
      return { _tag: "error", message: "Agent turn request failed." };
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      if (decodeFailed) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split("\n");
      buffer = parts.pop() ?? "";
      for (const part of parts) handle(part);
    }
    if (!decodeFailed && buffer.length > 0) handle(buffer);
    return outcome;
  } catch {
    // A terminal frame already parsed — a hiccup closing the connection is not this turn's failure.
    if (terminalReceived) return outcome;
    if (decodeFailed) return outcome;
    // Otherwise a mid-stream loss: the server-side drain keeps running detached, so the caller
    // reattaches rather than reporting a dead turn.
    return { _tag: "disconnected" };
  }
};

export const streamAgentTurn = async (
  body: {
    readonly documentId: string;
    readonly instruction: string;
    readonly skill?: string;
    readonly target?: string;
    // The rail's `[Fix]`/`[Fix all]` structured dispatch for an agentic-tagged finding (CON-1315,
    // Decision 8/10) — rides alongside `skill: "fix-findings"`, never with `target`.
    readonly fixScope?: {
      readonly ruleId: string;
      readonly headingPath?: readonly string[];
    };
    // The conversation a chat turn belongs to (D6, U4) — a client-minted id (see
    // `chat/thread-list-adapter.ts`'s `initialize`) or absent, in which case the route mints the
    // `legacy:<documentId>` sentinel (today's "always resume the latest chat" default). Never sent
    // for a direct `skill` invocation, which carries no conversation identity.
    readonly conversationId?: string;
    // Ad-hoc files dragged from the user's own filesystem, already streamed to the Anthropic Files
    // API — carried as id + name (+ the browser `File.type` so the harness attaches the right content
    // block), never inlined content (ADR-0055).
    readonly attachedDocuments?: readonly {
      readonly fileId: string;
      readonly name: string;
      readonly contentType?: string;
    }[];
  },
  handlers: AgentTurnHandlers,
  // U4: the assistant-ui `ChatModelAdapter`'s `run()` receives its own `abortSignal` (cancel / a
  // new message interrupting a run) that this function's internal timeout-driven `AbortController`
  // knows nothing about. Linking the two lets an external abort actually cancel the in-flight fetch
  // rather than leaving it to run to completion in the background.
  options?: { readonly signal?: AbortSignal },
): Promise<AgentTurnOutcome> => {
  const controller = new AbortController();
  if (options?.signal !== undefined) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const timeout = window.setTimeout(() => controller.abort(), AGENT_TURN_TIMEOUT_MS);
  try {
    const response = await fetch("/api/agent/turn", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return await consumeTurnStream(response, handlers);
  } catch {
    // The fetch itself threw (abort / network) before any terminal frame — a DISCONNECTION: the
    // server-side drain keeps running detached, so the caller reattaches rather than reporting dead.
    return { _tag: "disconnected" };
  } finally {
    window.clearTimeout(timeout);
  }
};

// Reattach to a conversation's in-flight turn over the read-only `/watch` NDJSON stream (ADR-0064,
// ADR-0042 reconnection) — the studio's replacement for the retired SSE tail over `session_event`.
// Never POSTs; the managed platform keeps the session running server-side, so this only observes. The
// same `AgentTurnHandlers`/`AgentTurnOutcome` contract as `streamAgentTurn`, so the model adapter
// folds a reattached turn exactly like a live one.
export const watchAgentTurn = async (
  conversationId: string,
  handlers: AgentTurnHandlers,
  options?: { readonly signal?: AbortSignal },
): Promise<AgentTurnOutcome> => {
  const controller = new AbortController();
  if (options?.signal !== undefined) {
    if (options.signal.aborted) controller.abort();
    else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
  }
  const timeout = window.setTimeout(() => controller.abort(), AGENT_TURN_TIMEOUT_MS);
  try {
    const response = await fetch(`/api/agent/conversations/${conversationId}/watch`, {
      signal: controller.signal,
    });
    return await consumeTurnStream(response, handlers);
  } catch {
    return { _tag: "disconnected" };
  } finally {
    window.clearTimeout(timeout);
  }
};

// The Expert's "Stop" for a chat turn: `POST /api/agent/turn/cancel` interrupts the in-flight turn's
// detached drain server-side. Fire-and-forget from the model adapter's abort handler — it deliberately
// does NOT await the claim being freed. The route's `cancelTurn` hands the interruption to a daemon
// fiber and responds immediately (`Fiber.interruptFork`, `agent-turn-route.ts`); the drain's `ensuring`
// release (which frees the per-conversation single-flight claim and sends the vendor `user.interrupt`)
// runs afterwards, on its own time. A follow-up turn sent right away can and will race that release —
// `model-adapter.ts` closes that race by RETRYING the follow-up's POST on the specific "a turn is
// already in flight" refusal, rather than this function blocking the composer on the cancel round-trip.
// Best-effort: a failed cancel resolves to void rather than throwing — the UI clears its running state
// off the turn generator's own cancelled yield regardless, and the turn's own timeouts bound a truly
// stuck drain.
export const cancelAgentTurn = async (conversationId: string): Promise<void> => {
  try {
    await fetch("/api/agent/turn/cancel", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ conversationId }),
    });
  } catch {
    // Best-effort — see the note above.
  }
};

// The dispatch outcomes `POST /api/agent/resolve-draft` reports (thread 3, U2/U5) — mirrors the
// server's `ResolverDispatchResult`, plus a client-only `"error"` for a failed/unreachable request so
// the button can distinguish "the server refused" from "the request never landed".
export type ResolveStaleDraftResult =
  | "dispatched"
  | "not-found"
  | "not-stale"
  | "attempts-exhausted"
  | "error";

// Ask the server to dispatch the stale-draft resolver for `draftId` (thread 3, U2). The resolver runs
// detached server-side; the UI observes the outcome (withdraw/rebase) over the live draft feed. The
// button uses the returned status only to reflect its own affordance (in-flight, exhausted). A failed
// or unparseable response degrades to `"error"` rather than throwing — the button re-enables so the
// Expert can retry (or fall back to a manual discard).
export const requestResolveStaleDraft = async (
  draftId: string,
): Promise<ResolveStaleDraftResult> => {
  try {
    const response = await fetch("/api/agent/resolve-draft", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ draftId }),
    });
    if (!response.ok) return "error";
    const body: unknown = await response.json();
    const status =
      typeof body === "object" && body !== null && "status" in body
        ? (body as { status: unknown }).status
        : undefined;
    return status === "dispatched" ||
      status === "not-found" ||
      status === "not-stale" ||
      status === "attempts-exhausted"
      ? status
      : "error";
  } catch {
    return "error";
  }
};

// The rail's `[Fix]`/`[Fix all]` outcomes (U11, CON-1315) — mirrors the server's `FixRouteResult`
// (`fix-route.ts`), plus a client-only `"error"` for a failed/unreachable/unparseable response, same
// shape as `ResolveStaleDraftResult` above. A successful "opened" hands back the new draft's id so the
// caller can react (the rail's own draft-feed subscription already renders it — the button just needs
// to know the request landed).
export type FixRuleResult =
  | { readonly status: "opened"; readonly draftId: string; readonly fixes: number }
  | { readonly status: "nothing-to-fix"; readonly fixes: number }
  | { readonly status: "error" };

// Ask the server to run `ruleId`'s fix producer(s) over `documentId`'s current head
// (`POST /api/curriculum/fix`) and fold the result into a fresh agent-owned draft. `headingPath`
// absent scopes to every occurrence of `ruleId` in the document (`[Fix all]`); present, to just that
// heading's occurrences (`[Fix]`) — the same QA#2 narrowing `dispatchFixFindings` below already gives
// the agentic path, so a deterministic and an agentic fix behave identically regardless of which
// button fired them.
const FixRuleSuccessBody = Schema.Union(
  Schema.Struct({
    status: Schema.Literal("opened"),
    draftId: Schema.String,
    fixes: Schema.Number,
  }),
  Schema.Struct({ status: Schema.Literal("nothing-to-fix"), fixes: Schema.Number }),
);

export const requestFixRule = async (
  documentId: string,
  ruleId: string,
  headingPath?: readonly string[],
): Promise<FixRuleResult> => {
  try {
    const response = await fetch("/api/curriculum/fix", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(
        headingPath === undefined ? { documentId, ruleId } : { documentId, ruleId, headingPath },
      ),
    });
    if (!response.ok) return { status: "error" };
    const body: unknown = await response.json();
    const decoded = Schema.decodeUnknownEither(FixRuleSuccessBody)(body);
    return Either.isRight(decoded) ? decoded.right : { status: "error" };
  } catch {
    return { status: "error" };
  }
};

// The rail's Cancel affordance on a running `[Fix]`/`[Fix all]` card (CON-1315 unit 2): stops the
// in-flight fix turn and discards its draft server-side (`cancelFixRun`, `agent-turn-route.ts`).
// Mirrors `requestFixRule`'s plain-`fetch` POST rather than `cancelReview`'s Effect-`HttpClient`
// call — `finding-queue.tsx`, the one caller, has no Effect runtime in scope. `not-cancellable` means
// the draft left `open` (submitted) between the click and the request landing; `not-running`/`error`
// are both no-ops — either way the WS `fix-progress` feed, not this response, is what actually drives
// the button's next state.
export type CancelFixRunResult = "cancelled" | "not-running" | "not-cancellable" | "error";

export const cancelFixRun = async (runId: string): Promise<CancelFixRunResult> => {
  try {
    const response = await fetch("/api/curriculum/fix/cancel", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ runId }),
    });
    if (!response.ok) return "error";
    const body: unknown = await response.json();
    const status =
      typeof body === "object" && body !== null && "status" in body
        ? (body as { status: unknown }).status
        : undefined;
    return status === "cancelled" || status === "not-running" || status === "not-cancellable"
      ? status
      : "error";
  } catch {
    return "error";
  }
};

// The rail's agentic `[Fix]`/`[Fix all]` dispatch (CON-1315, Decision 8/9/10) — a task-kind turn
// naming the `fix-findings` skill with a structured `fixScope`, for a finding whose `fix` is
// `agentic` (`requestFixRule` above is the sibling call for a `deterministic` finding instead).
// `headingPath` absent scopes to every occurrence of `ruleId` in the document (`[Fix all]`);
// present, to just that heading's occurrences (`[Fix]`). `instruction` is a short human-readable
// summary only — the skill itself reads `fixScope`, never free text — because the route rejects an
// empty instruction on every turn kind, task included.
export const dispatchFixFindings = (
  documentId: string,
  fixScope: { readonly ruleId: string; readonly headingPath?: readonly string[] },
  handlers: AgentTurnHandlers,
  options?: { readonly signal?: AbortSignal },
): Promise<AgentTurnOutcome> =>
  streamAgentTurn(
    {
      documentId,
      instruction:
        fixScope.headingPath === undefined
          ? `Fix ${fixScope.ruleId} findings`
          : `Fix ${fixScope.ruleId} findings in ${fixScope.headingPath.join(" > ")}`,
      skill: "fix-findings",
      fixScope,
    },
    handlers,
    options,
  );

// The server's authoritative answer to "is a turn actually in flight for this conversation right
// now?", read from the owner-scoped conversation summaries (`GET /api/agent/conversations?documentId=`
// — the same index the thread list uses). The `running` flag flips true server-side once a turn's
// managed session resolves and false when its detached drain finishes (`agent-turn-route.ts`), so it
// is the server's own record of whether the single-flight turn exists. Used to disambiguate a turn
// POST that DISCONNECTED without ever delivering a frame: only the server can tell "the request never
// landed" from "it landed and the stream died before the first byte". A running conversation means the
// turn landed (reattach over `/watch`); a not-running one — or one absent from the list — means it
// never landed, so the typed instruction is handed back to the composer instead. A failed/unreachable/
// unparseable check degrades to `false` (treat as never-landed): that is the text-protective default —
// the Expert keeps their message rather than being sent to a dead-end reconnect, and a genuinely
// in-flight turn still re-surfaces through the normal resume-on-mount path.
export const isConversationRunning = async (
  documentId: string,
  conversationId: string,
): Promise<boolean> => {
  try {
    const response = await fetch(
      `/api/agent/conversations?documentId=${encodeURIComponent(documentId)}`,
      { headers: { accept: "application/json" } },
    );
    if (!response.ok) return false;
    const decoded = Schema.decodeUnknownEither(Schema.Array(ConversationSummary))(
      await response.json(),
    );
    return (
      Either.isRight(decoded) &&
      decoded.right.some(
        (summary) => summary.conversationId === conversationId && summary.isRunning,
      )
    );
  } catch {
    return false;
  }
};

// Load one conversation's typed message projection (U3/U4:
// `GET /api/agent/conversations/:conversationId/messages`) — the `ThreadHistoryAdapter.load()`
// data source (`chat/history-adapter.ts`), read with the same raw-`fetch` + `Schema`-decode discipline
// `streamAgentTurn` uses for the turn stream (this is a plain JSON GET, not the NDJSON turn stream). A
// missing/failed/corrupt response degrades to an empty conversation rather than throwing — an
// unknown/empty `conversationId` (a brand-new, not-yet-turned thread) is expected to legitimately
// return `[]` from the server too.
//
// A plain history reader: the `conversationId` it receives is always a SERVER id — the history
// adapter resolves the thread's id through `resolveConversationId` (never minting) before calling
// here, so a still-local `__LOCALID_…` thread never reaches this function (it returns [] upstream).
// No prefix short-circuit lives here any more (ADR-0064 follow-up moved id resolution to the adapter);
// a genuinely unknown id degrades to `[]` on a non-2xx below.
export const loadConversationMessages = async (
  conversationId: string,
): Promise<readonly ConversationMessage[]> => {
  try {
    const response = await fetch(`/api/agent/conversations/${conversationId}/messages`, {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return [];
    const decoded = Schema.decodeUnknownEither(Schema.Array(ConversationMessage))(
      await response.json(),
    );
    return Either.isRight(decoded) ? decoded.right : [];
  } catch {
    return [];
  }
};
